/**
 * Normalise an MFA rejection into something the UI can act on (#594 D5/D6).
 *
 * Every failure used to arrive as the same sentence — "That code didn't work" —
 * whether the code was wrong, already used, correct-but-from-a-phone-whose-
 * clock-is-two-minutes-out, or typed against a QR the server had silently
 * replaced. The advice attached to that sentence ("try again") was right for
 * exactly one of them. For the rest it sent the user round a loop that, before
 * this change, ended in a permanently locked account.
 *
 * The backend now returns `detail: { code, message, ... }` for these, matching
 * the shape `routes/auth.py` already uses for `must_change_password`. Older
 * rejections — and everything outside this router — are still plain strings, so
 * both shapes are handled here rather than at each call site.
 */

import { errorText } from './apiErrors';

/** A pending token that has run out. Rendered as a page-level error, never as a
 *  code error: it is not the user's six digits that were wrong. */
export const EXPIRED_SESSION_CODES = new Set(['token_expired', 'attempts_exhausted']);

/** The enrollment itself is gone — the QR on screen is dead and must be replaced. */
export const STALE_ENROLLMENT_CODES = new Set(['enrollment_expired']);

/** Toasted after a recovery code was spent — at sign-in and at the in-session
 *  step-up (#854). One string so the two can never drift. */
export const RECOVERY_CODE_USED_MESSAGE = 'You used a recovery code. Set up a new one in Settings → Security.';

/**
 * @returns {{code: string, message: string, skewSeconds: number|null,
 *            attemptsLeft: number|null}}
 */
export function parseMfaError(err, fallback = "That code didn't work. Try again.") {
    const detail = err?.response?.data?.detail;

    // Arrays ARE objects in JS, so a FastAPI 422 `detail` reaches the branch
    // below and survives only by accident — it has no .code and no .message, so
    // it falls through to the fallback. Make that intentional and, via
    // errorText, actually report what the server rejected (#603). 'unknown' is
    // in neither EXPIRED_SESSION_CODES nor STALE_ENROLLMENT_CODES, which is
    // correct: a 422 is not a dead pending token.
    if (Array.isArray(detail)) {
        return { code: 'unknown', message: errorText(err, fallback), skewSeconds: null, attemptsLeft: null };
    }

    if (detail && typeof detail === 'object') {
        return {
            code: detail.code || 'unknown',
            // errorText returns detail.message when it is a usable string and
            // the fallback otherwise, so `message` can never be a non-string.
            message: errorText(err, fallback),
            skewSeconds: typeof detail.skew_seconds === 'number' ? detail.skew_seconds : null,
            attemptsLeft: typeof detail.attempts_left === 'number' ? detail.attempts_left : null,
        };
    }

    if (typeof detail === 'string') {
        // The pre-classification wire shape. "Token expired" comes straight from
        // decode_jwt_token and used to render verbatim under the code boxes,
        // where it was indistinguishable from a rejected code (D5).
        const expired = /token expired|session expired/i.test(detail);
        return {
            code: expired ? 'token_expired' : 'unknown',
            message: expired
                ? 'Your sign-in session timed out. Sign in again to restart setup.'
                : detail,
            skewSeconds: null,
            attemptsLeft: null,
        };
    }

    return { code: 'unknown', message: fallback, skewSeconds: null, attemptsLeft: null };
}

/**
 * Compare the browser clock to the server's.
 *
 * This measures the BROWSER, which is usually right even when the phone is
 * wrong — but it catches the common case (a desktop and a phone that are both
 * off, or a VM with a drifting clock) before the user types anything, and costs
 * nothing. The authoritative number still comes from the server's `classify`
 * on a failed code.
 *
 * Warn well inside the real boundary: TOTP tolerance is ±1 step, which in
 * practice is ±30s to ±59s depending on phase within the step.
 */
export const CLOCK_SKEW_WARN_MS = 20000;

export function clockSkewSeconds(serverTimeIso, now = Date.now()) {
    if (!serverTimeIso) return null;
    const serverMs = Date.parse(serverTimeIso);
    if (Number.isNaN(serverMs)) return null;
    const skewMs = now - serverMs;
    if (Math.abs(skewMs) <= CLOCK_SKEW_WARN_MS) return null;
    return Math.round(skewMs / 1000);
}

/**
 * "about 47 seconds fast".
 *
 * Exact seconds, never a coarsened band (user decision 2026-09-15): "more than
 * a minute fast" does not tell anyone what to set their clock to, and the whole
 * point of measuring the skew is to hand back a number that can be acted on.
 * Seconds also keep this wording identical to the server's own `classify`
 * message, so there is one message set rather than two to keep in step.
 */
export function describeSkew(seconds) {
    return `about ${Math.abs(seconds)} seconds ${seconds > 0 ? 'fast' : 'slow'}`;
}
